import { Link } from 'react-router-dom'
import { sileo } from 'sileo'
import UserAvatar from '../Shared/UserAvatar'
import { htmlToPlainText, sanitizeHtml } from '../../utils/richText'
import { employeeName } from '../../utils/employee'

/**
 * Detalle de una solución de la base de conocimiento: contenido formateado,
 * autor, fecha, tickets donde se aplicó y botón para copiar el procedimiento.
 *
 * @component
 * @param {Object} props
 * @param {Object|null} props.solution
 * @param {Object} [props.author] - Empleado que la registró.
 * @param {() => void} [props.onBack] - Volver a la lista (móvil).
 * @returns {JSX.Element}
 */
const SolutionDetail = ({ solution, author, onBack }) => {
  if (!solution) {
    return (
      <div className="sd-empty">
        <i className="fa-regular fa-lightbulb" />
        <span>Seleccione una solución para ver el procedimiento.</span>
      </div>
    )
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        `${solution.title}\n\n${htmlToPlainText(solution.description || '')}`
      )
      sileo.success({ title: 'Procedimiento copiado' })
    } catch {
      sileo.error({ title: 'No se pudo copiar' })
    }
  }

  return (
    <article className="sd">
      <header className="sd-head">
        {onBack && (
          <button
            type="button"
            className="sd-back"
            onClick={onBack}
            aria-label="Volver a la lista"
          >
            <i className="fa-solid fa-arrow-left" />
          </button>
        )}
        <div className="sd-titles">
          <span className="sd-meta">
            #{solution.id} ·{' '}
            {new Date(solution.createdAt).toLocaleDateString('es-CR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </span>
          <h5 className="sd-title">{solution.title}</h5>
        </div>
        <button type="button" className="sd-copy" onClick={copy}>
          <i className="fa-regular fa-copy" />
          <span>Copiar</span>
        </button>
      </header>

      <div className="sd-info">
        {author && (
          <span className="sd-chip">
            <UserAvatar employee={author} size={20} />
            {employeeName(author)}
          </span>
        )}
        {solution.ticketIds.length > 0 && (
          <span className="sd-tickets">
            <i className="fa-solid fa-ticket" />
            Aplicada en
            {solution.ticketIds.map((id) => (
              <Link key={id} to={`/ticket/${id}`} className="sd-ticket-link">
                #{id}
              </Link>
            ))}
          </span>
        )}
      </div>

      {solution.description ? (
        <div
          className="sd-content"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(solution.description),
          }}
        />
      ) : (
        <p className="sd-content text-body-secondary">Sin descripción.</p>
      )}

      <style>{`
        .sd-empty {
          height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 8px; padding: 32px; font-size: 0.85rem; color: var(--gm-text-faint); text-align: center;
        }
        .sd-empty i { font-size: 1.6rem; opacity: 0.6; }
        .sd { display: flex; flex-direction: column; gap: 12px; }
        .sd-head { display: flex; align-items: flex-start; gap: 10px; }
        .sd-titles { flex: 1; min-width: 0; }
        .sd-meta { font-size: 0.72rem; color: var(--gm-text-faint); font-variant-numeric: tabular-nums; }
        .sd-title { margin: 2px 0 0; font-size: 1.1rem; font-weight: 700; color: var(--gm-text); }
        .sd-back, .sd-copy {
          display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 10px; flex-shrink: 0;
          border: 1px solid var(--gm-border); border-radius: 8px; background: transparent;
          color: var(--gm-text-muted); font-size: 0.78rem; font-weight: 500;
        }
        .sd-back:hover, .sd-copy:hover { color: var(--gm-primary); border-color: var(--gm-primary); }
        .sd-info { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; font-size: 0.78rem; color: var(--gm-text-muted); }
        .sd-chip { display: inline-flex; align-items: center; gap: 6px; }
        .sd-tickets { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 6px; }
        .sd-ticket-link {
          padding: 1px 8px; border-radius: 99px; background: var(--gm-primary-soft); color: var(--gm-primary);
          font-weight: 600; text-decoration: none; font-variant-numeric: tabular-nums;
        }
        .sd-ticket-link:hover { background: var(--gm-primary); color: #fff; }
        .sd-content {
          padding-top: 12px; border-top: 1px solid var(--gm-border);
          font-size: 0.88rem; line-height: 1.6; color: var(--gm-text);
          white-space: pre-wrap; word-break: break-word;
        }
        .sd-content ul, .sd-content ol { margin: 0 0 0.5rem 1.1rem; padding-left: 0.4rem; white-space: normal; }
        .sd-content p { margin: 0 0 0.5rem; }
      `}</style>
    </article>
  )
}

export default SolutionDetail
